import type { RawAxiosRequestHeaders } from 'axios';
import { AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import { getAuth } from 'firebase/auth';

import { getAuthToken } from '@mopla/business-logic';
import { Api as ApiInterface, IRequestOptions } from '@mopla/data-models';

import { environment } from '../environments/environment';

import { axiosInstance } from './axiosConfig';

class Api implements ApiInterface {
	public baseURL: string;

	constructor(url: string) {
		this.baseURL = url;
	}

	private async request<Res = any>(
		path: string,
		postBody: any = {},
		method: Method = 'get',
		options: IRequestOptions = {}
	): Promise<Res> {
		const endpoint = this.baseURL + path;
		const token = await getAuthToken();

		const headers: RawAxiosRequestHeaders = {
			...options.headers,
		};

		// Only set Content-Type for non-FormData requests
		if (!(postBody instanceof FormData)) {
			headers['Content-Type'] = 'application/json';
		}

		if (token) {
			headers['Authorization'] = `Bearer ${token}`;
		}

		const { throwOnStatusCodes } = options;

		const response = await axiosInstance.request<Res>({
			url: endpoint,
			data: postBody,
			method,
			headers,
			...(throwOnStatusCodes &&
				throwOnStatusCodes.length > 0 && {
					/**
					 * Resolve only when the throwOnStatusCodes does NOT contain the received status
					 * @example
					 * throwOnStatusCodes = [403]
					 * status = 200 // api.*() will not throw
					 * status = 401 // api.*() will not throw
					 * status = 403 // api.*() will throw
					 * */
					validateStatus: (status) => !throwOnStatusCodes.includes(status),
				}),
		});

		return response.data;
	}

	public async makeRequest<Res = any>(
		path: string,
		config: AxiosRequestConfig
	): Promise<AxiosResponse<Res, any>> {
		const endpoint = this.baseURL + path;
		const token = await getAuth()?.currentUser?.getIdToken();
		const headers = config.headers ?? {};

		if (token) {
			headers['Authorization'] = `Bearer ${token}`;
		}

		const response = await axiosInstance.request<Res>({
			...config,
			url: endpoint,
			headers,
		});
		return response;
	}

	public get<Res = any>(
		path: string,
		options: IRequestOptions = {}
	): Promise<Res> {
		return this.request<Res>(path, {}, 'get', options);
	}

	public post<Res = any>(
		path: string,
		postBody: any = {},
		options: IRequestOptions = {}
	): Promise<Res> {
		return this.request<Res>(path, postBody, 'post', options);
	}

	public put<Res = any>(
		path: string,
		postBody: any = {},
		options: IRequestOptions = {}
	): Promise<Res> {
		return this.request<Res>(path, postBody, 'put', options);
	}

	public patch<Res = any>(
		path: string,
		postBody: any = {},
		options: IRequestOptions = {}
	): Promise<Res> {
		return this.request<Res>(path, postBody, 'patch', options);
	}

	public delete<Res = any>(
		path: string,
		postBody: any = {},
		options: IRequestOptions = {}
	): Promise<Res> {
		return this.request<Res>(path, postBody, 'delete', options);
	}
}

const passApi = new Api(environment.baseUrl);

export default passApi;
